import { useState, useEffect, useCallback } from "react";

/**
 * Custom hook to detect exit intent and manage exit intent popup
 * @param {boolean} isUserLoggedIn - Whether user is logged in
 * @param {boolean} hasActiveSubscription - Whether user has an active subscription
 * @param {Object} exitIntentCoupon - Coupon data from backend
 * @param {boolean} hasSubscriptionHistory - Whether user has ever subscribed before
 * @returns {Object} - { showExitPopup, setShowExitPopup, shouldShowPopup }
 */
export const useExitIntent = (isUserLoggedIn, hasActiveSubscription, exitIntentCoupon, hasSubscriptionHistory) => {
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [timeOnSite, setTimeOnSite] = useState(0);
  const [hasShownPopup, setHasShownPopup] = useState(false);

  // Check if user is eligible for exit intent popup
  const shouldShowPopup = useCallback(() => {
    // Don't show if already has subscription
    if (hasActiveSubscription) return false;
    
    // Don't show if user has EVER subscribed before (not a new user)
    if (hasSubscriptionHistory) {
      console.log("❌ User has subscription history - not eligible for exit intent offer");
      return false;
    }
    
    // Don't show if no coupon configured
    if (!exitIntentCoupon || !exitIntentCoupon.code) return false;
    
    // Don't show if already shown in this session
    if (hasShownPopup) return false;

    // Check if shown before in localStorage (optional - can remove if want to show every time)
    const lastShown = localStorage.getItem("exitIntentLastShown");
    if (lastShown) {
      const hoursSinceLastShown = (Date.now() - parseInt(lastShown)) / (1000 * 60 * 60);
      // Don't show again within 24 hours
      if (hoursSinceLastShown < 24) {
        console.log("❌ Popup shown within last 24 hours - cooldown active");
        return false;
      }
    }

    console.log("✅ User is eligible for exit intent popup");
    return true;
  }, [hasActiveSubscription, hasSubscriptionHistory, exitIntentCoupon, hasShownPopup]);

  // Track time on site
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeOnSite((prev) => prev + 1);
    }, 1000); // Increment every second

    return () => clearInterval(timer);
  }, []);

  // Show popup after 3 minutes (180 seconds) for logged-in users without subscription
  useEffect(() => {
    if (timeOnSite >= 180 && isUserLoggedIn && shouldShowPopup()) {
      setShowExitPopup(true);
      setHasShownPopup(true);
      localStorage.setItem("exitIntentLastShown", Date.now().toString());
    }
  }, [timeOnSite, isUserLoggedIn, shouldShowPopup]);

  // Detect mouse leaving viewport (exit intent)
  useEffect(() => {
    const handleMouseLeave = (e) => {
      // Check if mouse is leaving from the top
      if (e.clientY <= 0 && shouldShowPopup()) {
        setShowExitPopup(true);
        setHasShownPopup(true);
        localStorage.setItem("exitIntentLastShown", Date.now().toString());
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, [shouldShowPopup]);

  return {
    showExitPopup,
    setShowExitPopup,
    shouldShowPopup: shouldShowPopup(),
  };
};
